// Rutinas: pasos en orden, cada uno un encargo a un agente o una herramienta con argumentos fijos. Lo que deja un
// paso le llega al siguiente como dato. Sin ramas ni ciclos: si hace falta más, es un agente o es otro problema.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Cargando, Input, Modal, PageHead, Select, Tabla, Vacio } from '@glubi/ds';
import { api, mensajeDeError } from '../lib/api';
import { desde, proxima } from '../lib/formato';
import { useSesion } from '../lib/sesion';
import { Area, AvisoSoloVer, Casilla, Estado, SoloSiAdministra } from '../componentes/Comunes';
import { DetalleEjecucion, EditorHorario, horarioEnPalabras, invalidarTrabajo } from '../componentes/Trabajo';

type PasoForm =
  | { tipo: 'agente'; agenteId: string; encargo: string; alFallar: 'detener' | 'seguir' }
  | { tipo: 'herramienta'; herramienta: string; argumentos: string; sinAprobacion: boolean; alFallar: 'detener' | 'seguir' };
interface Form { id: string | null; nombre: string; descripcion: string; pasos: PasoForm[]; horario: { hora: string; dias: number[] } | null; estado: 'activo' | 'pausado' }

const vacia = (): Form => ({ id: null, nombre: '', descripcion: '', pasos: [], horario: null, estado: 'activo' });

export function Rutinas() {
  const { aviso } = useSesion();
  const qc = useQueryClient();
  const rutinas = useQuery({ queryKey: ['rutinas'], queryFn: () => api<any[]>('/api/rutinas'), refetchInterval: 15_000 });
  const agentes = useQuery({ queryKey: ['agentes'], queryFn: () => api<any[]>('/api/agentes') });
  const herramientas = useQuery({ queryKey: ['herramientas'], queryFn: () => api<any[]>('/api/herramientas') });
  const [form, setForm] = useState<Form | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [abierto, setAbierto] = useState<string | null>(null);

  const escribe = (nombre: string) => !herramientas.data?.find((t) => t.name === nombre)?.annotations?.readOnlyHint;
  const guardar = useMutation({
    mutationFn: async (f: Form) => {
      const pasos = f.pasos.map((p, i) => {
        if (p.tipo === 'agente') return p;
        let argumentos: Record<string, unknown> = {};
        if (p.argumentos.trim()) {
          try { argumentos = JSON.parse(p.argumentos); } catch { throw new Error(`Los argumentos del paso ${i + 1} no son JSON válido`); }
        }
        return { tipo: 'herramienta', herramienta: p.herramienta, argumentos, sinAprobacion: p.sinAprobacion, alFallar: p.alFallar };
      });
      return api<{ id: string }>(f.id ? `/api/rutinas/${f.id}` : '/api/rutinas', {
        nombre: f.nombre, descripcion: f.descripcion || null, pasos, horario: f.horario?.dias.length ? f.horario : null, estado: f.estado,
      });
    },
    onSuccess: (_r, f) => { invalidarTrabajo(qc); setForm(null); aviso(f.id ? 'Rutina cambiada' : 'Rutina creada'); },
    onError: (e) => setError(mensajeDeError(e)),
  });
  const estado = useMutation({
    mutationFn: ({ id, estado }: { id: string; estado: string }) => api(`/api/rutinas/${id}/estado`, { estado }),
    onSuccess: () => { invalidarTrabajo(qc); aviso('Listo'); },
    onError: (e) => aviso(mensajeDeError(e)),
  });
  const correr = useMutation({
    mutationFn: (id: string) => api<{ id: string }>(`/api/rutinas/${id}/correr`, {}),
    onSuccess: (r) => { invalidarTrabajo(qc); setAbierto(r.id); },
    onError: (e) => aviso(mensajeDeError(e)),
  });

  if (rutinas.isLoading) return <Cargando />;
  const nombreAgente = (id: string) => agentes.data?.find((a) => a.id === id)?.nombre ?? 'agente borrado';
  const cambiarPaso = (i: number, cambio: Partial<PasoForm>) => setForm((f) => f && { ...f, pasos: f.pasos.map((p, j) => (j === i ? { ...p, ...cambio } as PasoForm : p)) });
  const mover = (i: number, d: -1 | 1) => setForm((f) => {
    if (!f || i + d < 0 || i + d >= f.pasos.length) return f;
    const pasos = [...f.pasos];
    [pasos[i], pasos[i + d]] = [pasos[i + d]!, pasos[i]!];
    return { ...f, pasos };
  });
  const editar = (r: any) => {
    setError(null);
    setForm({
      id: r.id, nombre: r.nombre, descripcion: r.descripcion ?? '', horario: r.horario, estado: r.estado,
      pasos: r.pasos.map((p: any) => (p.tipo === 'agente' ? p : { ...p, argumentos: JSON.stringify(p.argumentos, null, 2) })),
    });
  };

  return (
    <div className="page">
      <PageHead title="Rutinas" subtitle="Trabajo de varios pasos que se repite: consultar, pasarle el resultado a un agente, escribir. Las escrituras piden aprobación, salvo las que se marquen a propósito.">
        <SoloSiAdministra><Button icon="plus" onClick={() => { setError(null); setForm(vacia()); }}>Nueva rutina</Button></SoloSiAdministra>
      </PageHead>
      <AvisoSoloVer />
      {!rutinas.data?.length ? (
        <Vacio titulo="Todavía no hay rutinas">Por ejemplo, el cierre de la noche: consultar el saldo de la caja, que el agente de cuadre lo revise y cerrar el día con aprobación.</Vacio>
      ) : (
        <Tabla rowKey="id" rows={rutinas.data}
          columns={[
            { key: 'nombre', label: 'Rutina', render: (r: any) => <div><b style={{ fontWeight: 500 }}>{r.nombre}</b>{r.descripcion && <div className="muted" style={{ fontSize: 12 }}>{r.descripcion}</div>}</div> },
            { key: 'pasos', label: 'Pasos', render: (r: any) => <div style={{ fontSize: 13 }}>{r.pasos.map((p: any) => (p.tipo === 'agente' ? nombreAgente(p.agenteId) : p.herramienta)).join(' → ')}</div> },
            { key: 'horario', label: 'Cuándo', render: (r: any) => <div style={{ fontSize: 13 }}>{horarioEnPalabras(r.horario)}{r.proximo_en && <div className="muted">la próxima: {proxima(r.proximo_en)}</div>}</div> },
            { key: 'ultima', label: 'Última vez', render: (r: any) => (r.ultima ? <button className="enlace" onClick={() => setAbierto(r.ultima.id)}>{desde(r.ultima.en)}</button> : <span className="muted">Nunca</span>) },
            { key: 'estado', label: 'Estado', render: (r: any) => (r.ultima && ['esperando_aprobacion', 'fallida'].includes(r.ultima.estado) ? <Estado estado={r.ultima.estado} /> : <Estado estado={r.estado} />) },
            { key: 'acciones', label: '', render: (r: any) => (
              <SoloSiAdministra>
                <div className="fila acciones">
                  <Button size="sm" icon="play" disabled={r.estado !== 'activo' || correr.isPending} onClick={() => correr.mutate(r.id)}>Correr</Button>
                  <Button size="sm" variant="secondary" icon="pencil" onClick={() => editar(r)}>Cambiar</Button>
                  <Button size="sm" variant="ghost" icon={r.estado === 'activo' ? 'pause' : 'play'} onClick={() => estado.mutate({ id: r.id, estado: r.estado === 'activo' ? 'pausado' : 'activo' })}>{r.estado === 'activo' ? 'Pausar' : 'Reactivar'}</Button>
                </div>
              </SoloSiAdministra>
            ) },
          ]} />
      )}

      {form && (
        <Modal title={form.id ? `Cambiar «${form.nombre}»` : 'Nueva rutina'} onClose={() => setForm(null)} width={780}
          subtitle="Los pasos corren en orden. Lo que deja uno le llega al siguiente agente como dato, nunca como instrucción."
          footer={<>
            <Button variant="ghost" onClick={() => setForm(null)}>Cancelar</Button>
            <Button disabled={guardar.isPending || form.nombre.trim().length < 2 || !form.pasos.length} onClick={() => { setError(null); guardar.mutate(form); }}>{form.id ? 'Guardar' : 'Crear rutina'}</Button>
          </>}>
          <div className="grid-2" style={{ gap: 12 }}>
            <Input label="Nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Cierre de la noche" autoFocus />
            <Input label="Para qué sirve" value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
          </div>
          <EditorHorario valor={form.horario} onChange={(horario) => setForm({ ...form, horario })} />

          <div className="stack" style={{ gap: 10 }}>
            <span className="etiqueta">Pasos</span>
            {form.pasos.map((p, i) => (
              <div key={i} className="operacion">
                <div className="entre">
                  <b style={{ fontWeight: 500, fontSize: 14 }}>Paso {i + 1} · {p.tipo === 'agente' ? 'encargo a un agente' : 'herramienta'}</b>
                  <div className="fila">
                    <Button size="sm" variant="ghost" icon="arrow-up" disabled={i === 0} onClick={() => mover(i, -1)}>Subir</Button>
                    <Button size="sm" variant="ghost" icon="trash" onClick={() => setForm({ ...form, pasos: form.pasos.filter((_, j) => j !== i) })}>Quitar</Button>
                  </div>
                </div>
                {p.tipo === 'agente' ? (<>
                  <Select label="Agente" value={p.agenteId} onChange={(e) => cambiarPaso(i, { agenteId: e.target.value })}
                    options={[...(p.agenteId ? [] : [{ value: '', label: 'Elegir…' }]), ...(agentes.data ?? []).map((a) => ({ value: a.id as string, label: a.nombre as string }))]} />
                  <Area label="Encargo" filas={2} value={p.encargo} onChange={(v) => cambiarPaso(i, { encargo: v })} placeholder="Revisa el saldo y di si cuadra con las ventas." />
                </>) : (<>
                  <Select label="Herramienta" value={p.herramienta} onChange={(e) => cambiarPaso(i, { herramienta: e.target.value })}
                    options={[...(p.herramienta ? [] : [{ value: '', label: 'Elegir…' }]), ...(herramientas.data ?? []).map((t) => ({ value: t.name as string, label: `${t.name}${t.annotations?.readOnlyHint ? '' : ' (escribe)'}` }))]} />
                  <Area label="Argumentos fijos (JSON)" mono filas={3} value={p.argumentos} onChange={(v) => cambiarPaso(i, { argumentos: v })} placeholder='{"dia": "2026-10-13"}' />
                  {p.herramienta && escribe(p.herramienta) && (
                    <Casilla checked={p.sinAprobacion} onChange={(v) => cambiarPaso(i, { sinAprobacion: v })}>
                      Escribir sin pedir aprobación (los argumentos son fijos: los escribió una persona)
                    </Casilla>
                  )}
                </>)}
                <Casilla checked={p.alFallar === 'seguir'} onChange={(v) => cambiarPaso(i, { alFallar: v ? 'seguir' : 'detener' })}>Si falla, seguir con el siguiente paso</Casilla>
              </div>
            ))}
            <div className="fila">
              <Button size="sm" variant="secondary" icon="bot" disabled={!agentes.data?.length} onClick={() => setForm({ ...form, pasos: [...form.pasos, { tipo: 'agente', agenteId: agentes.data?.[0]?.id ?? '', encargo: '', alFallar: 'detener' }] })}>Encargo a un agente</Button>
              <Button size="sm" variant="secondary" icon="plug" disabled={!herramientas.data?.length} onClick={() => setForm({ ...form, pasos: [...form.pasos, { tipo: 'herramienta', herramienta: '', argumentos: '{}', sinAprobacion: false, alFallar: 'detener' }] })}>Herramienta</Button>
            </div>
          </div>
          {error && <div className="error-caja">{error}</div>}
        </Modal>
      )}

      {abierto && <DetalleEjecucion id={abierto} onClose={() => setAbierto(null)} />}
    </div>
  );
}
